import {
  FormGroupLabel,
  FormRadioGroup,
  useDialogContext,
  useStoreState
} from "@ariakit/react";
import { findConditionalPurchaseOption } from "../../domain/keyword";
import { Unit } from "../../domain/unit";
import { atomic } from "../atomic.css";
import { CheckboxField } from "../checkbox-field";
import { useDatabase } from "../database-provider";
import { FormDialog } from "../form-dialog";
import { useForm } from "../hooks/use-form";

function DoubleKeywordButton(props: { active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className={atomic({
        border: "solid",
        fontFamily: "arboria",
        fontStyle: "italic",
        fontWeight: "black",
        paddingInline: 14,
        paddingBlock: 2,
        borderRadius: 16,
        transition: "background-color",
        backgroundColor: {
          default: props.active ? "text" : "background-primary",
          hover: props.active ? "text" : "background-secondary",
        },
        color: props.active ? "background-primary" : "text",
      })}
      onClick={props.onClick}
    >
      x2
    </button>
  );
}

export function UnitKeywordsDialog(props: { unit: Unit }) {
  const { unit } = props;
  const db = useDatabase();
  const store = useDialogContext();
  const form = useForm({
    defaultValues: {
      keywordIds: unit.keywords.map((k) => k.id),
      keywordDoubles: Object.fromEntries(
        unit.keywords.map((k) => [k.id, k.ref.count > 1]),
      ),
    },
    async onSubmit(values) {
      const { keywordIds, keywordDoubles } = values;

      await db.updateArmy({
        ...unit.army.data,
        units: unit.army.data.units.map((u) => {
          if (u.id === unit.data.id) {
            const keywordsRefs = keywordIds.map((id) => ({
              id,
              count: keywordDoubles[id] ? 2 : 1,
            }));

            // If an army keyword is present and, at the same time, the keyword was selected
            // as a double, make sure to include it in the unit's keyword refs. Otherwise,
            // make sure an army keyword is exluded.
            const keywords =
              unit.army.keyword && keywordDoubles[unit.army.keyword.id] ?
                keywordsRefs
              : keywordsRefs.filter((ref) => ref.id !== unit.army.keyword?.id);

            return { ...u, keywords };
          }

          return u;
        }),
      });

      store?.hide();
    },
  });
  const { keywordIds, keywordDoubles } = useStoreState(form, (s) => s.values);
  const hypotheticalUnit = Unit.from(unit.army, {
    ...unit.data,
    keywords: keywordIds.map((id) => {
      return { id, count: keywordDoubles[id] ? 2 : 1 };
    }),
  });

  return (
    <FormDialog form={form} heading="Keywords" submitLabel="Save">
      <FormRadioGroup>
        <FormGroupLabel
          className={atomic({
            borderBlockEnd: "solid",
            paddingBlockEnd: 6,
            fontWeight: "medium",
          })}
        >
          Unit keywords
        </FormGroupLabel>

        {Array.from(props.unit.ruleset.keywordDefs.values()).map((def) => {
          const conditionalCost =
            hypotheticalUnit ?
              findConditionalPurchaseOption(
                def.conditional_unit_costs,
                def.id,
                hypotheticalUnit,
              )?.cost
            : null;

          const cost = conditionalCost ?? def.unit_cost;
          const isDouble = keywordDoubles[def.id];
          const isSelected = keywordIds.includes(def.id);
          const doublesFormFieldName = form.names.keywordDoubles[def.id];

          return (
            <div
              key={def.id}
              className={atomic({
                borderBlockEnd: "dashed",
              })}
            >
              <div
                className={atomic({
                  display: "grid",
                  gridTemplateColumns: "fraction-auto",
                  paddingBlock: 16,
                  fontFamily: "arboria",
                  fontWeight: "black",
                  fontStyle: "italic",
                })}
              >
                <CheckboxField
                  name={form.names.keywordIds}
                  value={def.id}
                  className={atomic({ marginInlineEnd: 12 })}
                  disabled={def.id === props.unit.army.keyword?.id}
                  onClick={() => {
                    // When unselecting the checkbox, also unselect the double modifier
                    if (isSelected) {
                      form.setValue(doublesFormFieldName, false);
                    }
                  }}
                  label={def.name}
                />

                <div className={atomic({ display: "flex", gap: 8 })}>
                  {def.max_purchase_count > 1 && (
                    <DoubleKeywordButton
                      active={isDouble}
                      onClick={() => {
                        form.setValue(doublesFormFieldName, !isDouble);
                        if (!isSelected) {
                          form.pushValue(form.names.keywordIds, def.id);
                        }
                      }}
                    />
                  )}
                  <span>{cost === 0 ? "Free" : `${cost}pts`}</span>
                </div>
              </div>

              <div
                className={atomic({
                  fontSize: 14,
                  paddingBlockEnd: 16,
                  lineHeight: 1.5,
                })}
              >
                {def.description}
              </div>
            </div>
          );
        })}
      </FormRadioGroup>
    </FormDialog>
  );
}
